// Entitlements — per-user feature gating.
//
// Derived from the user's subscription plan + status, in ONE place so the
// Worker (enforcement) and the SPA (UI decoration) can never disagree. Gating
// is opt-in: a feature is gated only if it appears in MIN_PLAN *and* something
// actually calls can()/requireFeature() for it. Everything else is open.
//
// See docs/2026-06-28_entitlements-design.md.

export type Plan = "free" | "pro" | "team" | "enterprise";

export type Feature =
  | "resume"
  | "skills"
  | "analytics"
  | "teams"
  | "knowledge_base"
  | "work_tracking"
  | "governance";

export const FEATURES: Feature[] = [
  "resume",
  "skills",
  "analytics",
  "teams",
  "knowledge_base",
  "work_tracking",
  "governance",
];

const RANK: Record<Plan, number> = { free: 0, pro: 1, team: 2, enterprise: 3 };

// The single source of the plan -> feature mapping.
const MIN_PLAN: Record<Feature, Plan> = {
  resume: "pro",
  skills: "pro",
  analytics: "pro",
  teams: "team",
  knowledge_base: "team",
  work_tracking: "team",
  governance: "enterprise",
};

// The minimum plan a feature requires — used for upgrade-prompt copy.
export function minPlanFor(feature: Feature): Plan {
  return MIN_PLAN[feature];
}

export type SubRow = { plan?: string | null; status?: string | null } | null;

export interface Entitlement {
  plan: Plan;
  active: boolean;
  can: (feature: Feature) => boolean;
}

// The latest active/trialing subscription row -> what the user can do.
export function entitlement(sub: SubRow): Entitlement {
  const active = sub?.status === "active" || sub?.status === "trialing";
  const plan: Plan = active && sub?.plan ? (sub.plan as Plan) : "free";
  return {
    plan,
    active: plan !== "free",
    can: (feature: Feature) => RANK[plan] >= RANK[MIN_PLAN[feature]],
  };
}

// Serializable view for the API response / client cache: plan + a boolean per
// feature, computed with the same logic above so the wire format can't drift.
export type FeatureFlags = Record<Feature, boolean>;

export interface EntitlementView {
  plan: Plan;
  features: FeatureFlags;
}

export function entitlementView(sub: SubRow): EntitlementView {
  const e = entitlement(sub);
  const features = {} as FeatureFlags;
  for (const f of FEATURES) features[f] = e.can(f);
  return { plan: e.plan, features };
}
